import { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { usePxToVw } from "../../hooks/usePxToVw";
import Button from "../backend/buttonApp/Button";
import WayfinderDialog from "./WayfinderDialog";
import LoadingOverlay from "./LoadingOverlay";
import { keyboardLayouts } from "./KeyboardLayoutsComplete";
import WheelchairToggle from "../WheelchairToggle";
import WeatherWidget from "../WeatherWidget";

const WayfinderSearchScreenFixed = ({
  brandColor,
  brandFont,
  onBack,
  searchSettings,
  searchData,
  onItemSelect,
  backButton,
  timeoutPopupSettings,
  userId, // Add userId prop for fetching ordered locations
  buttonId, // NEW: Button ID for button-specific wayfinder config
  categoryIds, // NEW: Filter by these category IDs (optional, can be derived from buttonId)
  subCategoryIds, // NEW: Filter by these subcategory IDs (optional, can be derived from buttonId)
  resetSession, // Add resetSession function from parent
  selectedLanguage,
  wheelchairSettings = { enabled: false }, // NEW: Wheelchair settings from parent
  weatherSettings = { enabled: false }, // NEW: Weather settings from parent
  isWheelchairMode = false, // NEW: Receive from parent
  onWheelchairToggle, // NEW: Receive handler from parent
  wayfinderGlobalSoundSettings = { clickSound: "", clickSoundVolume: 1 }, // NEW: Global sound settings
}) => {

  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false); // New loading state for search
  const [timeoutOpen, setTimeoutOpen] = useState(false);
  const [timeoutCountdown, setTimeoutCountdown] = useState(5);
  const [keyboardMode, setKeyboardMode] = useState("lowercase"); // 'lowercase', 'uppercase', 'numeric', 'special'
  const [allLocations, setAllLocations] = useState([]); // Store all locations for search
  const [globalSoundSettings, setGlobalSoundSettings] = useState(null); // Store global sound settings

  const searchInputRef = useRef(null);
  const timeoutRef = useRef(null);
  const countdownRef = useRef(null);
  const searchTimeoutRef = useRef(null); // For debouncing

  const { pxToVw } = usePxToVw();

  console.log("timeout popup Settings", timeoutPopupSettings);

  // Helper function to get language-specific text from Map or object
  const getLanguageText = (textField, fallback = "") => {
    if (!textField) return fallback;

    // If it's already a string, return it
    if (typeof textField === 'string') return textField;

    // If it's an object (Map sent as JSON from backend)
    if (typeof textField === 'object' && textField !== null) {
      // Try selected language
      if (textField[selectedLanguage]) return textField[selectedLanguage];

      // Fallback to English
      if (textField['en']) return textField['en'];

      // Fallback to first available language
      const firstKey = Object.keys(textField)[0];
      if (firstKey && textField[firstKey]) return textField[firstKey];
    }

    return fallback;
  };

  // Helper function to get language-specific content from searchBox.languages
  const getLanguageContent = () => {
    if (!searchSettings?.searchBox?.languages) {
      // No languages object, return searchBox as-is (legacy format)
      return searchSettings?.searchBox || {};
    }

    const languages = searchSettings.searchBox.languages;

    // Try to get content for selected language
    if (languages[selectedLanguage]) {
      return languages[selectedLanguage];
    }

    // Fallback to English
    if (languages['en']) {
      return languages['en'];
    }

    // Fallback to first available language
    const firstKey = Object.keys(languages)[0];
    if (firstKey && languages[firstKey]) {
      return languages[firstKey];
    }

    // Return empty object if no language content found
    return {};
  };

  // Get language-specific content
  const languageContent = getLanguageContent();



  // Set global sound settings from prop
  useEffect(() => {
    if (wayfinderGlobalSoundSettings.clickSound !== '' && wayfinderGlobalSoundSettings) {
      setGlobalSoundSettings(wayfinderGlobalSoundSettings);
    } else {
      const fetchGlobalSoundSettings = async () => {
        if (!userId) return;

        try {
          const response = await fetch(`/api/wayfinder/global-wayfinder-button-settings/${userId}`);
          const data = await response.json();

          if (data.success && data.data?.globalSettings) {
            setGlobalSoundSettings(data.data.globalSettings);
          } else {
            console.log("Failed to fetch global sound settings or no settings found");
          }
        } catch (error) {
          console.error("Error fetching global sound settings:", error);
        }
      };

      fetchGlobalSoundSettings();
    }


  }, [userId]);

  // Sound playback functions
  const playClickSound = () => {
    if (globalSoundSettings?.clickSound) {
      try {

        const audio = new Audio(globalSoundSettings.clickSound);
        audio.volume = globalSoundSettings.clickSoundVolume || 1;


        audio.play().catch(error => {
          console.log("Could not play click sound:", error);
        });
      } catch (error) {
        console.log("Error creating click sound audio:", error);
      }
    }
  };

  const playKeyboardClickSound = () => {
    if (globalSoundSettings?.keyboardClickSound) {
      try {
        const audio = new Audio(globalSoundSettings.keyboardClickSound);
        audio.volume = globalSoundSettings.keyboardClickSoundVolume || 1;
        audio.play().catch(error => {
          console.log("Could not play keyboard click sound:", error);
        });
      } catch (error) {
        console.log("Error creating keyboard click sound audio:", error);
      }
    } else {
      // Play default keyboard click sound using Web Audio API
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
        oscillator.type = 'sine';

        gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);

        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.1);
      } catch (error) {
        console.log("Could not play default keyboard click sound:", error);
      }
    }
  };

  const getCurrentKeyboardLayout = () => {
    const layout = keyboardLayouts[selectedLanguage] || keyboardLayouts.en;
    return keyboardMode === "uppercase" ? layout.uppercase : layout.lowercase;
  };


  // Provide default settings if searchSettings is undefined
  let safeSearchSettings = searchSettings ? { ...searchSettings } : {
    searchBox: {
      enabled: true,
      headerText: "Search",
      placeholder: "Search...",
      backgroundColor: "#ffffff",
      textColor: "#000000",
      opacity: 1,
      padding: "12",
      width: "300",
      headerPositionTop: "10",
      searchInputPositionTop: "50",
      buttonMargin: "20",
      keyboardSpacing: "40",
      keyboardPositionBottom: "2",
      wheelchairContentOffsetY: 20, // Wheelchair mode content offset percentage
      border: {
        width: "1",
        color: "#cccccc",
        radius: "8",
      },
      boxShadow: {
        shadowAngle: 45,
        shadowSize: 5,
        shadowDistance: 2,
        shadowAlpha: 1,
        shadowColor: "#000000",
      },
      keyboard: {
        backgroundColor: "#ffffff",
        borderWidth: "1",
        borderColor: "#e0e0e0",
        borderRadius: "12",
        keyBackgroundColor: "#ffffff",
        keyBorderWidth: "1",
        keyBorderColor: "#e0e0e0",
        keyBorderRadius: "8",
        keyTextColor: "#322727",
        shadow: {
          shadowAngle: 45,
          shadowSize: 5,
          shadowDistance: 2,
          shadowAlpha: 0.15,
          shadowColor: "#000000",
        },
      },
      uploadedUrls: {
        upperLeft: { enabled: false, fileUrl: "", width: 100 },
        upperRight: { enabled: false, fileUrl: "", width: 100 },
        bottomLeft: { enabled: false, fileUrl: "", width: 100 },
        bottomRight: { enabled: false, fileUrl: "", width: 100 },
      },
      centerLogo: {
        fileUrl: "",
        fileName: "",
        enabled: false,
        width: "100",
      },
    },
  };

  // Merge language-specific content into safeSearchSettings
  if (languageContent && Object.keys(languageContent).length > 0) {
    // Create a new searchBox object with merged content
    safeSearchSettings = {
      ...safeSearchSettings,
      searchBox: {
        ...safeSearchSettings.searchBox,
        // Override language-specific fields with content from the selected language
        headerText: languageContent.headerText || safeSearchSettings.searchBox.headerText,
        uploadedUrls: languageContent.uploadedUrls || safeSearchSettings.searchBox.uploadedUrls,
        centerLogo: languageContent.centerLogo || safeSearchSettings.searchBox.centerLogo,
      }
    };
  }


  // Get wheelchair content offset from settings (after safeSearchSettings is defined)
  const wheelchairContentOffsetY = safeSearchSettings?.searchBox?.wheelchairContentOffsetY || 20;

  // Fetch ordered locations from backend
  useEffect(() => {
    const fetchOrderedLocations = async () => {
      // If searchData is already provided with locations, use it directly (for subcategory/category specific locations)
      if (searchData && Array.isArray(searchData) && searchData.length > 0) {

        const limitedData = applyOrderingAndLimit(searchData);

        setAllLocations(limitedData);
        setSearchResults(limitedData);
        return;
      }

      if (!userId) {
        console.log(
          "No userId provided and no searchData, cannot fetch locations",
        );
        return;
      }

      try {

        // Use the wayfinderButton API to get button-specific locations
        let url;
        if (buttonId) {
          // Use button-specific wayfinder configuration
          url = `/api/wayfinder-button/locations/${userId}/${buttonId}/${selectedLanguage}`;
        } else {
          // Fallback to locationOrder API with category/subcategory filtering
          const params = new URLSearchParams();
          if (buttonId) {
            params.append('buttonId', buttonId);
          }
          if (categoryIds && categoryIds.length > 0) {
            params.append('categoryIds', JSON.stringify(categoryIds));
          }
          if (subCategoryIds && subCategoryIds.length > 0) {
            params.append('subCategoryIds', JSON.stringify(subCategoryIds));
          }
          const queryString = params.toString();
          url = `/api/locationOrder/get-location-order/frontend/${userId}${queryString ? `?${queryString}` : ''}`;
        }

        const response = await fetch(url);
        const data = await response.json();

        if (data.success && data.data) {
          // The wayfinderButton API returns locations in the correct format already
          if (buttonId) {
            // Data from wayfinderButton API is already processed and ordered
            const limitedData = data.data.slice(0, 8); // Limit to 8 for search screen
            setAllLocations(limitedData);
            setSearchResults(limitedData);
          } else {
            // Data from locationOrder API needs processing
            const limitedData = applyOrderingAndLimit(data.data);
            setAllLocations(limitedData);
            setSearchResults(limitedData);
          }
        } else {
          console.log("Failed to fetch wayfinder locations:", data.message);
          setAllLocations([]);
          setSearchResults([]);
        }
      } catch (error) {
        console.error("Error fetching wayfinder locations:", error);
        setAllLocations([]);
        setSearchResults([]);
      }
    };

    fetchOrderedLocations();
  }, [userId, buttonId, searchData, categoryIds, subCategoryIds]);

  // Helper function to apply ordering and limit to 8 buttons
  const applyOrderingAndLimit = (locations) => {
    if (!locations || !Array.isArray(locations)) return [];



    // Process locations to ensure proper data structure
    const processedLocations = locations.map((item) => {
      // Handle different data structures from API
      let locationData = item;

      // If item has locationId property (from locationOrder API)
      if (item.locationId) {
        locationData = {
          ...item.locationId,
          order: item.order,
        };
      }

      // Ensure required properties exist
      return {
        id: locationData._id || locationData.id,
        _id: locationData._id || locationData.id,
        name:
          locationData.name || locationData.buttonText || "Unnamed Location",
        buttonText:
          locationData.buttonText || locationData.name || "Unnamed Location",
        order: locationData.order,
        type: "location",
        spaceName: locationData.spaceName,
        photo:
          locationData.photo ||
          locationData.uploadedUrls?.locationPhoto?.fileUrl,
        globalSettings: locationData.globalSettings || {
          fillColor: "#4a7c59",
          fontColor: "#ffffff",
          outlineColor: "#4a7c59",
          outlineThickness: 2,
          cornerRadius: 12,
          fontSize: 16,
          fontWeight: 600,
          padding: 18,
          width: "100%",
          minHeight: 60,
        },
        customSettings: locationData.customSettings || {},
        custom: locationData.custom || false,
      };
    });

    console.log("processedLocations", processedLocations);

    // ONLY include locations that have explicit order values (not null/undefined)
    let locationsWithOrders = processedLocations.filter(
      (loc) => loc.order !== null && loc.order !== undefined
    );



    // Sort by order (ascending - lower numbers first)
    const sorted = [...locationsWithOrders].sort((a, b) => {
      if (a.order !== b.order) {
        return a.order - b.order;
      }
      // If orders are the same, sort alphabetically
      const nameA = (a.name || a.buttonText || "").toLowerCase();
      const nameB = (b.name || b.buttonText || "").toLowerCase();
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
      return 0;
    });



    // Limit to maximum 8 buttons
    const limited = sorted.slice(0, 8);

    return limited;
  };

  // Debounced search function
  const debouncedSearch = useCallback((term) => {
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // If term is empty, immediately show all locations without debouncing
    if (!term.trim()) {

      setSearchResults(allLocations);
      setIsSearching(false);
      return;
    }

    // Set new timeout for debouncing (300ms delay)
    searchTimeoutRef.current = setTimeout(() => {
      performSearch(term);
    }, 300);
  }, [allLocations]);

  // Perform search with ordering and limit
  const performSearch = async (term) => {
    setIsSearching(true);

    try {
      if (!term.trim()) {
        setSearchResults(allLocations); // allLocations is already limited to 8 ordered locations
        setIsSearching(false);
        return;
      }

      // Always use backend API for search to search through ALL locations, not just the 8 displayed
      if (!userId) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }


      let url;
      if (buttonId) {
        // Use button-specific wayfinder search API - this searches ALL locations in the button config
        const params = new URLSearchParams();
        params.append('searchTerm', encodeURIComponent(term));

        url = `/api/wayfinder-button/search/${userId}/${buttonId}/${selectedLanguage}?${params.toString()}`;
      } else {
        // Fallback to locationOrder search API with category/subcategory filtering
        const params = new URLSearchParams();
        params.append('searchTerm', term);

        if (buttonId) {
          params.append('buttonId', buttonId);
        }
        if (categoryIds && categoryIds.length > 0) {
          params.append('categoryIds', JSON.stringify(categoryIds));
        }
        if (subCategoryIds && subCategoryIds.length > 0) {
          params.append('subCategoryIds', JSON.stringify(subCategoryIds));
        }

        url = `/api/locationOrder/search-ordered-locations/${userId}?${params.toString()}`;
      }


      const response = await fetch(url);
      const data = await response.json();

      if (data.success && data.data) {
        // Results are already filtered and limited by backend
        setSearchResults(data.data);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error("Error performing backend search:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Handle search input change with debouncing
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Clear any pending debounced search first
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // If the search term is empty, immediately show all locations
    if (!value.trim()) {
      setSearchResults(allLocations);
      setIsSearching(false);
      return;
    }

    // Set searching state immediately for better UX
    setIsSearching(true);

    // Use debounced search for better performance
    debouncedSearch(value);
  };

  // Handle keyboard input with debouncing
  const handleKeyboardEnter = (value) => {
    // Clear debounce timeout and search immediately on enter
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    performSearch(value);
  };

  // Handle individual key presses from custom keyboard
  const handleKeyboardKey = (key) => {
    // Play keyboard click sound
    playKeyboardClickSound();

    let newValue = searchTerm;

    if (key === " ") {
      // Space key
      newValue = searchTerm + " ";
    } else if (key === "backspace") {
      // Backspace key
      newValue = searchTerm.slice(0, -1);
    } else if (key === "shift") {
      // Toggle between lowercase and uppercase
      setKeyboardMode((prev) =>
        prev === "lowercase" ? "uppercase" : "lowercase",
      );
      return;
    } else if (key === "123") {
      // Switch to numeric mode
      setKeyboardMode("numeric");
      return;
    } else if (key === "ABC") {
      // Switch back to letter mode
      setKeyboardMode("lowercase");
      return;
    } else if (key === "#+=") {
      // Switch to special characters mode
      setKeyboardMode("special");
      return;
    } else {
      // Regular character - apply case based on mode
      let char = key;
      if (keyboardMode === "uppercase" && typeof key === "string") {
        char = key.toUpperCase();
      }
      newValue = searchTerm + char;
    }

    // Update search term directly
    setSearchTerm(newValue);

    // Keep focus on the input
    // if (searchInputRef.current) {
    //   searchInputRef.current.focus();
    // }

    // Clear any pending debounced search first
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // If the search term is empty after the key press, immediately show all locations
    if (!newValue.trim()) {
      setSearchResults(allLocations);
      setIsSearching(false);
      return;
    }

    // Set searching state immediately for better UX
    setIsSearching(true);

    // Use debounced search for better performance
    debouncedSearch(newValue);
  };



  // Handle item selection - simplified for location search
  const handleItemSelect = (item) => {


    // Play click sound
    // playClickSound();

    // Regular item selection for locations
    setIsLoading(true);

    if (onItemSelect) {
      onItemSelect(item);
    } else {
      console.error("onItemSelect function is not provided!");
    }

    setTimeout(() => setIsLoading(false), 500);
  };

  // Handle back button
  const handleBack = () => {
    clearAllTimers();
    if (onBack) {
      onBack();
    }
  };

  // Clear all timers
  const clearAllTimers = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
  };

  // Start 30-second inactivity timeout
  const startInactivityTimeout = () => {
    clearAllTimers();

    const timeoutDuration = timeoutPopupSettings?.timeoutDuration || 30; // Default to 30 seconds if not set

    console.log("Starting 30-second inactivity timeout");
    timeoutRef.current = setTimeout(() => {
      console.log("30 seconds of inactivity - showing timeout popup");
      setTimeoutOpen(true);
      startCountdown();
    }, timeoutDuration * 1000);
  };

  // Start countdown when popup opens
  const startCountdown = () => {
    setTimeoutCountdown(5);

    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }

    countdownRef.current = setInterval(() => {
      setTimeoutCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          handleTimeoutComplete();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Handle timeout complete - call resetSession instead of going to main menu
  const handleTimeoutComplete = () => {
    setTimeoutOpen(false);
    clearAllTimers();
    if (resetSession) {
      resetSession();
    } else if (onBack) {
      // Fallback to onBack if resetSession is not provided
      onBack('mainMenu');
    }
  };

  // Handle user clicking "OK" button
  const handleContinue = () => {
    setTimeoutOpen(false);
    clearAllTimers();

    // Start new 30-second timeout
    startInactivityTimeout();
  };

  // Reset timeout on user interaction
  const resetTimeout = () => {
    if (!timeoutOpen) {
      startInactivityTimeout();
    }
  };

  // Initialize timeout when component mounts and auto-focus search input
  useEffect(() => {

    startInactivityTimeout();

    // Auto-focus the search input when component mounts
    // if (searchInputRef.current) {
    //   // Small delay to ensure DOM is ready
    //   setTimeout(() => {
    //     searchInputRef.current?.focus();
    //   }, 100);
    // }

    return () => {
      clearAllTimers();
    };
  }, []);

  // Listen for user interactions to reset timeout
  useEffect(() => {
    const events = ["mousedown", "keydown", "touchstart", "scroll"];

    const handleInteraction = () => {
      if (!timeoutOpen) {
        resetTimeout();
      }
    };

    events.forEach((event) => {
      window.addEventListener(event, handleInteraction);
    });

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleInteraction);
      });
    };
  }, [timeoutOpen]);

  // Initialize search results with all data and handle updates
  useEffect(() => {
    if (allLocations && Array.isArray(allLocations)) {
      // Only update search results if search term is empty
      if (!searchTerm.trim()) {
        setSearchResults(allLocations);
      }
    }
  }, [allLocations, searchTerm]);

  // Box shadow helper
  const getBoxShadow = (boxShadow = {}) => {
    const {
      shadowAngle = 0,
      shadowSize = 5,
      shadowDistance = 2,
      shadowAlpha = 0.2,
      shadowColor = "#000000",
    } = boxShadow;

    const degToRad = (deg) => deg * (Math.PI / 180);
    const rad = degToRad(shadowAngle);
    const x = Math.cos(rad) * shadowDistance;
    const y = Math.sin(rad) * shadowDistance;

    const hexToRgba = (hex, alpha = 1) => {
      let c = hex.replace("#", "");
      if (c.length === 3) {
        c = c
          .split("")
          .map((x) => x + x)
          .join("");
      }
      const r = parseInt(c.substring(0, 2), 16);
      const g = parseInt(c.substring(2, 4), 16);
      const b = parseInt(c.substring(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    const rgbaColor = hexToRgba(shadowColor, shadowAlpha);
    const blur = shadowSize * 2;
    const spread = shadowSize * 0.5;
    return `${x}px ${y}px ${blur}px ${spread}px ${rgbaColor}`;
  };

  // Get keyboard key style
  const getKeyStyle = (isSpecialKey = false) => {
    const keyboardSettings = safeSearchSettings?.searchBox?.keyboard || {};

    return {
      width: "100%",
      height: "5vw",
      minHeight: "5vw",
      aspectRatio: "1 / 1",
      backgroundColor: keyboardSettings.keyBackgroundColor || "#ffffff",
      border: `${keyboardSettings.keyBorderWidth || "1"}px solid ${isSpecialKey ? "#5a6268" : keyboardSettings.keyBorderColor || "#e0e0e0"}`,
      borderRadius: `${keyboardSettings.keyBorderRadius || "8"}px`,
      fontSize: "2vw",
      fontWeight: "600",
      color: keyboardSettings.keyTextColor || "#322727",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "all 0.2s ease",
      boxShadow: getBoxShadow(keyboardSettings.shadow || {}),
      padding: 0,
    };
  };

  // Render search results in fixed 2x4 grid with consistent positioning
  const renderSearchResults = () => {


    if (searchResults.length === 0) {
      return null;
    }

    // Create a fixed 8-button grid (2 rows x 4 columns)
    const buttonGrid = Array(8).fill(null);

    // Fill the grid with available results
    searchResults.slice(0, 8).forEach((item, index) => {
      buttonGrid[index] = item;
    });

    // Split into 2 rows of 4 buttons each
    const row1 = buttonGrid.slice(0, 4);
    const row2 = buttonGrid.slice(4, 8);

    // Get button margin from settings (default 20px)
    const buttonMargin = safeSearchSettings?.searchBox?.buttonMargin || "20";

    const renderButton = (item, index) => {

      // const itemWidth = item?.custom
      //   ? item?.customSettings?.width
      //   : item?.globalSettings?.width || 300;

      const itemWidth = searchResults[0]?.custom
        ? searchResults[0]?.customSettings?.width
        : searchResults[0]?.globalSettings?.width || 300;


      if (!item) {
        // Empty placeholder button
        return (
          <div
            key={`empty-${index}`}
            style={{
              width: pxToVw(itemWidth),
              height: pxToVw(itemWidth),
              visibility: "hidden", // Keep space but invisible
            }}
          />
        );
      }

      // Resolve language-specific settings for Button component
      const resolveSettings = (settings) => {
        if (!settings) return settings;

        const resolved = { ...settings };

        // Resolve btnPng if it's an object with language keys
        if (resolved.btnPng && typeof resolved.btnPng === 'object') {
          resolved.btnPng = resolved.btnPng[selectedLanguage] || resolved.btnPng['en'] || null;
        }

        // Resolve btnPngFileName if it's an object with language keys
        if (resolved.btnPngFileName && typeof resolved.btnPngFileName === 'object') {
          resolved.btnPngFileName = resolved.btnPngFileName[selectedLanguage] || resolved.btnPngFileName['en'] || null;
        }

        return resolved;
      };

      const buttonSettings = item.custom
        ? resolveSettings(item.customSettings)
        : resolveSettings(item.globalSettings) || {
          fillColor: "#4a7c59",
          fontColor: "#ffffff",
          outlineColor: "#4a7c59",
          outlineThickness: 2,
          cornerRadius: 12,
          fontSize: 16,
          fontWeight: 600,
          padding: 18,
          width: "100%",
          minHeight: 60,
        };

      return (
        <div
          key={`button-${item._id || item.id || index}`}
          onClick={() => handleItemSelect(item)}
          style={{
            cursor: "pointer",
            width: pxToVw(
              item.custom
                ? item.customSettings?.width
                : item.globalSettings?.width || 200,
            ),
            height: pxToVw(
              item.custom
                ? item.customSettings?.height
                : item.globalSettings?.height || 200,
            ),
          }}
        >
          <Button
            text={item.buttonText[selectedLanguage] || item.name[selectedLanguage]}
            settings={
              buttonSettings
            }

            wayfinder={true}
            wayfinderClickSound={item?.clickSound ? item?.clickSound : globalSoundSettings?.clickSound}
            clickSoundVolume={item?.clickSoundVolume ? item?.clickSoundVoume : globalSoundSettings?.clickSoundVolume || 1}

          />
        </div>
      );
    };

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          marginTop: pxToVw(
            safeSearchSettings?.searchBox?.buttonMarginTop || "40",
          ),
          padding: "0 20px",
        }}
      >
        {/* First Row */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: pxToVw(buttonMargin),
            width: "100%",
            marginBottom: pxToVw(buttonMargin),
          }}
        >
          {row1.map((item, index) => renderButton(item, index))}
        </div>

        {/* Second Row */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: pxToVw(buttonMargin),
            width: "100%",
          }}
        >
          {row2.map((item, index) => renderButton(item, index + 4))}
        </div>

        {/* No results message */}
        {searchResults.length === 0 && (
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              color: "#666",
              fontSize: pxToVw(24),
            }}
          >
            {searchTerm
              ? `No results found for "${searchTerm}"`
              : "No locations available"}
          </div>
        )}
      </div>
    );
  };

  if (!safeSearchSettings) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontSize: pxToVw(24),
          color: "#666",
        }}
      >
        Loading search settings...
      </div>
    );
  }

  return (
    <div
      style={{
        width: "100%",
        minHeight: "100vh",
        position: "relative",
        fontFamily: brandFont || "Arial, sans-serif",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        paddingTop: isWheelchairMode ? `${wheelchairContentOffsetY}%` : "40px",
        paddingBottom: "40px",
        backgroundColor: "#ffffff",
        overflowY: "auto",
        transition: "padding-top 0.3s ease",
      }}
    >
      {/* Add CSS for search spinner animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>

      {/* Wheelchair Toggle Button */}
      <WheelchairToggle
        settings={wheelchairSettings}
        isActive={isWheelchairMode}
        onClick={onWheelchairToggle}
      />

      {/* Weather Widget */}
      {weatherSettings?.enabled && weatherSettings?.visibility?.wayfinderSearchScreen && (
        <div
          style={{
            position: 'absolute',
            top: weatherSettings.positionTop !== null && weatherSettings.positionTop !== undefined ? `${weatherSettings.positionTop}%` : 'auto',
            bottom: weatherSettings.positionBottom !== null && weatherSettings.positionBottom !== undefined ? `${weatherSettings.positionBottom}%` : 'auto',
            ...(weatherSettings.horizontalAlign === 'center'
              ? { left: '50%', right: 'auto', transform: 'translateX(-50%)' }
              : {
                left: weatherSettings.positionLeft !== null && weatherSettings.positionLeft !== undefined ? `${weatherSettings.positionLeft}%` : 'auto',
                right: weatherSettings.positionRight !== null && weatherSettings.positionRight !== undefined ? `${weatherSettings.positionRight}%` : 'auto',
              }
            ),
            zIndex: 1000,
          }}
        >
          <WeatherWidget
            apiKey={weatherSettings.weatherApiKey}
            latitude={weatherSettings.latitude}
            longitude={weatherSettings.longitude}
            forecastDays={weatherSettings.forecastDays || 3}
            temperatureUnit={weatherSettings.temperatureUnit || 'both'}
            celsiusFirst={weatherSettings.celsiusFirst !== false}
            showLocation={weatherSettings.showLocation || false}
            showClock={weatherSettings.showClock !== false}
            showCurrentWeather={weatherSettings.showCurrentWeather !== false}
            showForecast={weatherSettings.showForecast !== false}
            widgetOrder={weatherSettings.widgetOrder || ['clock', 'current', 'forecast']}
            backgroundColor={weatherSettings.backgroundColor || '#ffffff'}
            backgroundOpacity={weatherSettings.backgroundOpacity ?? 80}
            textColor={weatherSettings.textColor || '#1a202c'}
            subTextColor={weatherSettings.subTextColor || '#718096'}
            padding={weatherSettings.padding ?? 16}
            borderRadius={weatherSettings.borderRadius ?? 12}
            width={weatherSettings.width || 0}
            fontFamily={weatherSettings.fontFamily === 'inherit' || !weatherSettings.fontFamily ? brandFont || 'inherit' : weatherSettings.fontFamily}
            fontSize={weatherSettings.fontSize || 14}
            iconSize={weatherSettings.iconSize || 40}
            forecastIconSize={weatherSettings.forecastIconSize || 28}
            shadowH={weatherSettings.shadowH ?? 0}
            shadowV={weatherSettings.shadowV ?? 4}
            shadowBlur={weatherSettings.shadowBlur ?? 12}
            shadowSpread={weatherSettings.shadowSpread ?? 0}
            shadowColor={weatherSettings.shadowColor || '#000000'}
            shadowOpacity={weatherSettings.shadowOpacity ?? 15}
            useVwSizing={true}
          />
        </div>
      )}

      {/* Back Button */}
      {backButton?.enabled && backButton.custom === false && backButton.buttonImage?.fileUrl ? (
        <div
          className="corner-item back-button"
        >
          <img
            src={backButton?.buttonImage?.fileUrl}
            alt="Back"
            onClick={handleBack}
            style={{
              width:
                backButton?.buttonImage?.width > 0
                  ? `${backButton?.buttonImage?.width}%`
                  : "auto",
              cursor: "pointer",
            }}
          />
        </div>
      ) :
        (
          <motion.div
            className="corner-item back-button"
            style={{
              position: "fixed",
              top: backButton?.positionTop + "%" || "50%",
              left: backButton?.positionLeft + "%" || "0%",
              cursor: "pointer",
              zIndex: 10,
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <button
              onClick={handleBack}
              style={{
                backgroundColor: backButton.backgroundColor || "#000000",
                color: backButton.textColor || "#ffffff",
                border: `${backButton.borderWidth || 1}px solid ${backButton.borderColor || "#000000"}`,
                borderRadius: `${backButton.borderRadius?.topLeft || 8}px`,
                padding: `${backButton.paddingTopBottom || 12}px ${backButton.paddingLeftRight || 24}px`,
                fontSize: pxToVw(backButton.fontSize || 16),
                fontWeight: backButton.fontWeight || 500,
                cursor: "pointer",
              }}
            >
              {backButton.text || "Back"}
            </button>
          </motion.div>
        )}

      {/* Center Logo */}
      {safeSearchSettings?.searchBox?.centerLogo?.enabled &&
        safeSearchSettings?.searchBox?.centerLogo?.fileUrl && (
          <div
            style={{
              marginTop: `${safeSearchSettings?.searchBox?.centerLogo?.positionTop || 10}vh`,
              // position: "fixed",
              // top: "50%",
              // left: "50%",
              // transform: "translate(-50%, -50%)",
              zIndex: 5,
              display: "flex",
              justifyContent: "center",
              width: "100%",
              // maxWidth: "50vw",
            }}
          >
            <img
              src={safeSearchSettings.searchBox.centerLogo.fileUrl}
              alt="Center logo"
              style={{
                width:
                  safeSearchSettings.searchBox.centerLogo.width > 0
                    ? `${safeSearchSettings.searchBox.centerLogo.width}%`
                    : "auto",
                //  width: `${safeSearchSettings.searchBox.centerLogo.width !== 0 ? safeSearchSettings.searchBox.centerLogo.width + '%' : 'unset'}`,
                // height: "auto",
                objectFit: "contain",
              }}
            />
          </div>
        )}

      {/* Corner Logos */}
      <div className="corner-overlays-responsive">
        {safeSearchSettings?.searchBox?.uploadedUrls &&
          Object.entries(safeSearchSettings.searchBox.uploadedUrls).map(
            ([position, logo]) => {
              if (!logo?.enabled || !logo?.fileUrl) return null;

              // const positionStyles = {
              //   upperLeft: { top: "3%", left: "3%" },
              //   upperRight: { top: "3%", right: "3%" },
              //   bottomLeft: { bottom: "25%", left: "3%" },
              //   bottomRight: { bottom: "25%", right: "3%" },
              // };

              const keyMap = {
                upperLeft: "top-left",
                upperRight: "top-right",
                bottomLeft: "bottom-left",
                bottomRight: "bottom-right",
              };

              const justfyContentMap = {
                upperLeft: "start",
                upperRight: "end",
                bottomLeft: "start",
                bottomRight: "end",
              };

              return (
                <div
                  className={`corner-item ${keyMap[position] || position}`}
                  key={keyMap[position] || position} // 👈 custom key here
                  style={{
                    position: "fixed",
                    // ...positionStyles[position],
                    zIndex: 10,
                    maxWidth: "25vw",
                    justifyContent: justfyContentMap[position],
                  }}
                >
                  <img
                    src={logo.fileUrl}
                    alt={`${position} logo`}
                    style={{
                      width: logo.width + "%",
                      height: "auto",
                      objectFit: "contain",
                    }}
                  />
                </div>
              );
            },
          )}
      </div>

      {/* Header */}
      {safeSearchSettings?.searchBox?.headerText && (
        <motion.div
          style={{
            textAlign: "center",
            marginTop: `${safeSearchSettings?.searchBox?.headerPositionTop || 5}%`,
            marginBottom: "30px",
            width: "100%",
            maxWidth: pxToVw(safeSearchSettings?.searchBox?.width || 800),
            padding: "0 20px",
          }}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1
            style={{
              fontSize: pxToVw(
                safeSearchSettings?.searchBox?.headerFontSize || 32,
              ),
              fontWeight:
                safeSearchSettings?.searchBox?.headerFontWeight || 600,
              color: safeSearchSettings?.searchBox?.headerColor || "#000000",
              fontFamily:
                safeSearchSettings?.searchBox?.headerFontFamily ||
                brandFont ||
                "Arial",
              margin: 0,
              lineHeight: 1.3,
            }}
          >
            {safeSearchSettings.searchBox.headerText}
          </h1>
        </motion.div>
      )}

      {/* Search Box */}
      <motion.div
        style={{
          position: "relative",
          // marginTop: `${safeSearchSettings?.searchBox?.searchInputPositionTop || 2}%`,
          width: "100%",
          maxWidth: pxToVw(safeSearchSettings?.searchBox?.width || 800),
          padding: "0 20px",
        }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        <div
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
          }}
        >
          <input
            ref={searchInputRef}
            type="text"
            value={searchTerm}
            onChange={handleSearch}
            placeholder={
              getLanguageText(
                safeSearchSettings?.searchBox?.placeholder,
                "Search..."
              )
            }
            readOnly // Prevent device keyboard from appearing
            // inputMode="none" // Additional prevention for mobile keyboards
            // autoComplete="off" // Disable autocomplete
            style={{
              backgroundColor:
                safeSearchSettings?.searchBox?.backgroundColor || "#ffffff",
              color: safeSearchSettings?.searchBox?.textColor || "#000000",
              opacity: safeSearchSettings?.searchBox?.opacity || 1,
              padding: pxToVw(safeSearchSettings?.searchBox?.padding || 20),
              paddingRight: pxToVw(60), // Add space for loading indicator
              width: "100%",
              border: `${safeSearchSettings?.searchBox?.border?.width || 1}px solid ${safeSearchSettings?.searchBox?.border?.color || "#cccccc"}`,
              borderRadius: `${safeSearchSettings?.searchBox?.border?.radius || 8}px`,
              boxShadow: getBoxShadow(
                safeSearchSettings?.searchBox?.boxShadow || {},
              ),
              fontSize: pxToVw(safeSearchSettings?.searchBox?.fontSize || 24),
              outline: "none",
              fontFamily: "Arial, sans-serif",
              transition: "border-color 0.3s ease, box-shadow 0.3s ease",
              cursor: "pointer", // Show pointer cursor since it's readonly
              caretColor: "transparent", // Hide the blinking cursor
            }}
            onFocus={(e) => {
              e.target.style.borderColor = brandColor || "#0066cc";
              e.target.style.boxShadow = `0 0 0 2px ${brandColor || "#0066cc"}20`;
            }}
            onBlur={(e) => {
              e.target.style.borderColor =
                safeSearchSettings?.searchBox?.border?.color || "#cccccc";
              e.target.style.boxShadow = getBoxShadow(
                safeSearchSettings?.searchBox?.boxShadow || {},
              );
            }
            }

          />

          {/* Invisible overlay to prevent direct clicks on input */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "transparent",
              cursor: "default",
              zIndex: 99,
            }}
            onClick={(e) => {

              e.preventDefault();
              e.stopPropagation();
            }}
            onTouchStart={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          />

          {/* Search Loading Indicator */}
          {isSearching && (
            <div
              style={{
                position: "absolute",
                right: pxToVw(20),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 11, // Above the invisible overlay
                pointerEvents: "none", // Don't block clicks
              }}
            >
              <div
                style={{
                  width: pxToVw(20),
                  height: pxToVw(20),
                  border: `2px solid ${brandColor || "#0066cc"}30`,
                  borderTop: `2px solid ${brandColor || "#0066cc"}`,
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                }}
              />
            </div>
          )}
        </div>
      </motion.div>

      {/* Search Results */}
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          // marginBottom: pxToVw(safeSearchSettings?.searchBox?.keyboardSpacing || '40'),

          height: `calc(${pxToVw(safeSearchSettings?.searchBox?.buttonMarginTop || 40)} + ${pxToVw(200)} + ${pxToVw(safeSearchSettings?.searchBox?.buttonMargin || 20)} + ${pxToVw(200)})`,
          flexShrink: 0, // never compress this area
          alignItems: "flex-start",
        }}
      >
        {renderSearchResults()}
      </div>

      <div
        style={{

          position: "relative",
          marginTop: `${safeSearchSettings?.searchBox?.keyboardPositionBottom || "2"}%`,
          width: "70vw",
          backgroundColor:
            safeSearchSettings?.searchBox?.keyboard?.backgroundColor ||
            "#ffffff",
          borderRadius: `${safeSearchSettings?.searchBox?.keyboard?.borderRadius || "12"}px`,
          border: `${safeSearchSettings?.searchBox?.keyboard?.borderWidth || "1"}px solid ${safeSearchSettings?.searchBox?.keyboard?.borderColor || "#e0e0e0"}`,
          padding: "2vw",
          boxShadow: getBoxShadow(
            safeSearchSettings?.searchBox?.keyboard?.shadow || {},
          ),
          zIndex: 5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {keyboardMode === "numeric" || keyboardMode === "special" ? (
          // Numeric & Special Characters Keyboard
          <>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.8vw",
                width: "100%",
                margin: "0 auto",
                boxSizing: "border-box",
                height: "100%",
                justifyContent: "center",
              }}
            >
              {/* Row 1 */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(10, 1fr)",
                  gap: "0.6vw",
                  width: "100%",
                }}
              >
                {(keyboardMode === "numeric"
                  ? ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"]
                  : ["!", "@", "#", "$", "%", "^", "&", "*", "(", ")"]
                ).map((key) => (
                  <button
                    key={key}
                    onClick={() => handleKeyboardKey(key)}
                    style={getKeyStyle(false)}
                    onMouseDown={(e) =>
                      (e.currentTarget.style.backgroundColor = "#f0f0f0")
                    }
                    onMouseUp={(e) =>
                    (e.currentTarget.style.backgroundColor =
                      safeSearchSettings?.searchBox?.keyboard
                        ?.keyBackgroundColor || "#ffffff")
                    }
                    onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor =
                      safeSearchSettings?.searchBox?.keyboard
                        ?.keyBackgroundColor || "#ffffff")
                    }
                  >
                    {key}
                  </button>
                ))}
              </div>

              {/* Row 2 */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(10, 1fr)",
                  gap: "0.6vw",
                  width: "100%",
                }}
              >
                {(keyboardMode === "numeric"
                  ? ["-", "/", ":", ";", "(", ")", "$", "&", "@", '"']
                  : ["-", "_", "=", "+", "[", "]", "{", "}", "\\", "|"]
                ).map((key) => (
                  <button
                    key={key}
                    onClick={() => handleKeyboardKey(key)}
                    style={getKeyStyle(false)}
                    onMouseDown={(e) =>
                      (e.currentTarget.style.backgroundColor = "#f0f0f0")
                    }
                    onMouseUp={(e) =>
                    (e.currentTarget.style.backgroundColor =
                      safeSearchSettings?.searchBox?.keyboard
                        ?.keyBackgroundColor || "#ffffff")
                    }
                    onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor =
                      safeSearchSettings?.searchBox?.keyboard
                        ?.keyBackgroundColor || "#ffffff")
                    }
                  >
                    {key}
                  </button>
                ))}
              </div>

              {/* Row 3 */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.5fr repeat(8, 1fr) 1.5fr",
                  gap: "0.6vw",
                  width: "100%",
                }}
              >
                <button
                  onClick={() =>
                    handleKeyboardKey(
                      keyboardMode === "numeric" ? "#+=" : "123",
                    )
                  }
                  style={{ ...getKeyStyle(true), fontSize: "1.6vw" }}
                  onMouseDown={(e) =>
                    (e.currentTarget.style.backgroundColor = "#5a6268")
                  }
                  onMouseUp={(e) =>
                    (e.currentTarget.style.backgroundColor = "#6c757d")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "#6c757d")
                  }
                >
                  {keyboardMode === "numeric" ? "#+=" : "123"}
                </button>
                {(keyboardMode === "numeric"
                  ? [".", ",", "?", "!", "'", '"', "<", ">"]
                  : [":", ";", "'", '"', ",", ".", "<", ">"]
                ).map((key) => (
                  <button
                    key={key}
                    onClick={() => handleKeyboardKey(key)}
                    style={getKeyStyle(false)}
                    onMouseDown={(e) =>
                      (e.currentTarget.style.backgroundColor = "#f0f0f0")
                    }
                    onMouseUp={(e) =>
                    (e.currentTarget.style.backgroundColor =
                      safeSearchSettings?.searchBox?.keyboard
                        ?.keyBackgroundColor || "#ffffff")
                    }
                    onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor =
                      safeSearchSettings?.searchBox?.keyboard
                        ?.keyBackgroundColor || "#ffffff")
                    }
                  >
                    {key}
                  </button>
                ))}
                <button
                  onClick={() => handleKeyboardKey("backspace")}
                  style={{ ...getKeyStyle(false), fontSize: "1.6vw" }}
                  onMouseDown={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f0f0f0")
                  }
                  onMouseUp={(e) =>
                  (e.currentTarget.style.backgroundColor =
                    safeSearchSettings?.searchBox?.keyboard
                      ?.keyBackgroundColor || "#ffffff")
                  }
                  onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor =
                    safeSearchSettings?.searchBox?.keyboard
                      ?.keyBackgroundColor || "#ffffff")
                  }
                >
                  Del
                </button>
              </div>

              {/* Bottom Control Row */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.5fr 5fr 1.5fr",
                  gap: "0.6vw",
                  width: "100%",
                }}
              >
                <button
                  onClick={() => handleKeyboardKey("ABC")}
                  style={{ ...getKeyStyle(true), fontSize: "1.6vw" }}
                  onMouseDown={(e) =>
                    (e.currentTarget.style.backgroundColor = "#5a6268")
                  }
                  onMouseUp={(e) =>
                    (e.currentTarget.style.backgroundColor = "#6c757d")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "#6c757d")
                  }
                >
                  ABC
                </button>
                <button
                  onClick={() => handleKeyboardKey(" ")}
                  style={{
                    ...getKeyStyle(false),
                    height: "4.5vw",
                    fontSize: "1.6vw",
                  }}
                  onMouseDown={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f0f0f0")
                  }
                  onMouseUp={(e) =>
                  (e.currentTarget.style.backgroundColor =
                    safeSearchSettings?.searchBox?.keyboard
                      ?.keyBackgroundColor || "#ffffff")
                  }
                  onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor =
                    safeSearchSettings?.searchBox?.keyboard
                      ?.keyBackgroundColor || "#ffffff")
                  }
                >
                  space
                </button>
                <button
                  onClick={() => handleKeyboardEnter(searchTerm)}
                  style={{ ...getKeyStyle(false), fontSize: "1.6vw" }}
                  onMouseDown={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f0f0f0")
                  }
                  onMouseUp={(e) =>
                  (e.currentTarget.style.backgroundColor =
                    safeSearchSettings?.searchBox?.keyboard
                      ?.keyBackgroundColor || "#ffffff")
                  }
                  onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor =
                    safeSearchSettings?.searchBox?.keyboard
                      ?.keyBackgroundColor || "#ffffff")
                  }
                >
                  return
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.9vw",
                width: "100%",
                margin: "0 auto",
                boxSizing: "border-box",
                height: "100%",
                justifyContent: "center",
              }}
            >
              {/* Top Row - Numbers */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(10, 1fr)",
                  gap: "0.6vw",
                  width: "100%",
                }}
              >
                {["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"].map(
                  (key) => (
                    <button
                      key={key}
                      onClick={() => handleKeyboardKey(key)}
                      style={getKeyStyle(false)}
                      onMouseDown={(e) =>
                        (e.currentTarget.style.backgroundColor = "#f0f0f0")
                      }
                      onMouseUp={(e) =>
                      (e.currentTarget.style.backgroundColor =
                        safeSearchSettings?.searchBox?.keyboard
                          ?.keyBackgroundColor || "#ffffff")
                      }
                      onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor =
                        safeSearchSettings?.searchBox?.keyboard
                          ?.keyBackgroundColor || "#ffffff")
                      }
                    >
                      {key}
                    </button>
                  ),
                )}
              </div>

              {/* Second Row - First row of letters */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: `repeat(${(getCurrentKeyboardLayout()[0] || []).length}, 1fr) 1.4fr`,
                  gap: "0.6vw",
                  width: "100%",
                }}
              >
                {(getCurrentKeyboardLayout()[0] || ["q", "w", "e", "r", "t", "y", "u", "i", "o", "p"]).map(
                  (key) => (
                    <button
                      key={key}
                      onClick={() => handleKeyboardKey(key)}
                      style={getKeyStyle(false)}
                      onMouseDown={(e) =>
                        (e.currentTarget.style.backgroundColor = "#f0f0f0")
                      }
                      onMouseUp={(e) =>
                      (e.currentTarget.style.backgroundColor =
                        safeSearchSettings?.searchBox?.keyboard
                          ?.keyBackgroundColor || "#ffffff")
                      }
                      onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor =
                        safeSearchSettings?.searchBox?.keyboard
                          ?.keyBackgroundColor || "#ffffff")
                      }
                    >
                      {key}
                    </button>
                  ),
                )}
                <button
                  onClick={() => handleKeyboardKey("backspace")}
                  style={{ ...getKeyStyle(false), fontSize: "1.8vw" }}
                  onMouseDown={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f0f0f0")
                  }
                  onMouseUp={(e) =>
                  (e.currentTarget.style.backgroundColor =
                    safeSearchSettings?.searchBox?.keyboard
                      ?.keyBackgroundColor || "#ffffff")
                  }
                  onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor =
                    safeSearchSettings?.searchBox?.keyboard
                      ?.keyBackgroundColor || "#ffffff")
                  }
                >
                  Del
                </button>
              </div>

              {/* Third Row - Second row of letters */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: `repeat(${(getCurrentKeyboardLayout()[1] || []).length}, 1fr)`,
                  gap: "0.6vw",
                  width: "100%",
                  paddingLeft: "3vw",
                  paddingRight: "3vw",
                }}
              >
                {(getCurrentKeyboardLayout()[1] || ["a", "s", "d", "f", "g", "h", "j", "k", "l"]).map((key) => (
                  <button
                    key={key}
                    onClick={() => handleKeyboardKey(key)}
                    style={getKeyStyle(false)}
                    onMouseDown={(e) =>
                      (e.currentTarget.style.backgroundColor = "#f0f0f0")
                    }
                    onMouseUp={(e) =>
                    (e.currentTarget.style.backgroundColor =
                      safeSearchSettings?.searchBox?.keyboard
                        ?.keyBackgroundColor || "#ffffff")
                    }
                    onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor =
                      safeSearchSettings?.searchBox?.keyboard
                        ?.keyBackgroundColor || "#ffffff")
                    }
                  >
                    {key}
                  </button>
                ))}
              </div>

              {/* Fourth Row - Third row of letters with Shift */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: `1.3fr repeat(${(getCurrentKeyboardLayout()[2] || []).length}, 1fr)`,
                  gap: "0.6vw",
                  width: "100%",
                }}
              >
                <button
                  onClick={() => handleKeyboardKey("shift")}
                  style={{
                    ...getKeyStyle(true),
                    fontSize: "2.5vw",
                    backgroundColor:
                      keyboardMode === "uppercase"
                        ? safeSearchSettings?.searchBox?.keyboard
                          ?.keyBackgroundColor || "#007bff"
                        : safeSearchSettings?.searchBox?.keyboard
                          ?.keyBackgroundColor || "#6c757d",
                  }}
                  onMouseDown={(e) =>
                    (e.currentTarget.style.backgroundColor = "#5a6268")
                  }
                  onMouseUp={(e) =>
                  (e.currentTarget.style.backgroundColor =
                    keyboardMode === "uppercase"
                      ? safeSearchSettings?.searchBox?.keyboard
                        ?.keyBackgroundColor || "#007bff"
                      : safeSearchSettings?.searchBox?.keyboard
                        ?.keyBackgroundColor || "#6c757d")
                  }
                  onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor =
                    keyboardMode === "uppercase"
                      ? safeSearchSettings?.searchBox?.keyboard
                        ?.keyBackgroundColor || "#007bff"
                      : safeSearchSettings?.searchBox?.keyboard
                        ?.keyBackgroundColor || "#6c757d")
                  }
                >
                  ⇧
                </button>
                {(getCurrentKeyboardLayout()[2] || ["z", "x", "c", "v", "b", "n", "m"]).map((key) => (
                  <button
                    key={key}
                    onClick={() => handleKeyboardKey(key)}
                    style={getKeyStyle(false)}
                    onMouseDown={(e) =>
                      (e.currentTarget.style.backgroundColor = "#f0f0f0")
                    }
                    onMouseUp={(e) =>
                    (e.currentTarget.style.backgroundColor =
                      safeSearchSettings?.searchBox?.keyboard
                        ?.keyBackgroundColor || "#ffffff")
                    }
                    onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor =
                      safeSearchSettings?.searchBox?.keyboard
                        ?.keyBackgroundColor || "#ffffff")
                    }
                  >
                    {key}
                  </button>
                ))}
              </div>

              {/* Fifth Row - Fourth row of letters (for languages like Hindi) */}
              {getCurrentKeyboardLayout()[3] && getCurrentKeyboardLayout()[3].length > 0 && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: `repeat(${getCurrentKeyboardLayout()[3].length}, 1fr)`,
                    gap: "0.6vw",
                    width: "100%",
                    paddingLeft: "3vw",
                    paddingRight: "3vw",
                  }}
                >
                  {getCurrentKeyboardLayout()[3].map((key) => (
                    <button
                      key={key}
                      onClick={() => handleKeyboardKey(key)}
                      style={getKeyStyle(false)}
                      onMouseDown={(e) =>
                        (e.currentTarget.style.backgroundColor = "#f0f0f0")
                      }
                      onMouseUp={(e) =>
                      (e.currentTarget.style.backgroundColor =
                        safeSearchSettings?.searchBox?.keyboard
                          ?.keyBackgroundColor || "#ffffff")
                      }
                      onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor =
                        safeSearchSettings?.searchBox?.keyboard
                          ?.keyBackgroundColor || "#ffffff")
                      }
                    >
                      {key}
                    </button>
                  ))}
                </div>
              )}

              {/* Bottom Control Row */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.2fr 3.5fr 1.3fr",
                  gap: "0.6vw",
                  width: "100%",
                }}
              >
                <button
                  onClick={() => handleKeyboardKey("123")}
                  style={{ ...getKeyStyle(false), fontSize: "1.8vw" }}
                  onMouseDown={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f0f0f0")
                  }
                  onMouseUp={(e) =>
                  (e.currentTarget.style.backgroundColor =
                    safeSearchSettings?.searchBox?.keyboard
                      ?.keyBackgroundColor || "#ffffff")
                  }
                  onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor =
                    safeSearchSettings?.searchBox?.keyboard
                      ?.keyBackgroundColor || "#ffffff")
                  }
                >
                  123
                </button>
                <button
                  onClick={() => handleKeyboardKey(" ")}
                  style={{ ...getKeyStyle(false), fontSize: "1.8vw" }}
                  onMouseDown={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f0f0f0")
                  }
                  onMouseUp={(e) =>
                  (e.currentTarget.style.backgroundColor =
                    safeSearchSettings?.searchBox?.keyboard
                      ?.keyBackgroundColor || "#ffffff")
                  }
                  onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor =
                    safeSearchSettings?.searchBox?.keyboard
                      ?.keyBackgroundColor || "#ffffff")
                  }
                >
                  space
                </button>
                <button
                  onClick={() => handleKeyboardEnter(searchTerm)}
                  style={{ ...getKeyStyle(false), fontSize: "1.8vw" }}
                  onMouseDown={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f0f0f0")
                  }
                  onMouseUp={(e) =>
                  (e.currentTarget.style.backgroundColor =
                    safeSearchSettings?.searchBox?.keyboard
                      ?.keyBackgroundColor || "#ffffff")
                  }
                  onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor =
                    safeSearchSettings?.searchBox?.keyboard
                      ?.keyBackgroundColor || "#ffffff")
                  }
                >
                  return
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <LoadingOverlay isLoading={isLoading} />

      {/* Timeout Dialog */}
      <WayfinderDialog
        open={timeoutOpen}
        onClose={handleContinue}
        settings={{
          ...timeoutPopupSettings,
          text: {
            ...timeoutPopupSettings?.text,
            content: getLanguageText(
              timeoutPopupSettings?.text?.content,
              'Are you still there?'
            ),
          },
          subtitle: {
            ...timeoutPopupSettings?.subtitle,
            content: getLanguageText(
              timeoutPopupSettings?.subtitle?.content,
              'Returning to main menu in...'
            ),
          },
          button: {
            ...timeoutPopupSettings?.button,
            text: getLanguageText(
              timeoutPopupSettings?.button?.text,
              "Yes, I'm Here"
            ),
          },
        }}
        brandColor={brandColor}
        brandFont={brandFont}
        countdown={timeoutCountdown}
        showCountdown={true}
        resetSession={resetSession}
      />
    </div>
  );
};

export default WayfinderSearchScreenFixed;